import { io, Socket } from "socket.io-client";
import { logger } from "@/utils";
import {
  Component,
  Operation,
  OperationType,
  Project,
  User,
  VectorClock,
  VersionConflict,
} from "@/types";
import { hapticFeedbackService } from "@/services/mobile/HapticFeedbackService";

export interface CursorPosition {
  x: number;
  y: number;
}

export interface SocketClientConfig {
  url: string;
  token: string;
  userId: string;
  reconnectionAttempts?: number;
  reconnectionDelay?: number;
  timeout?: number;
  mobileOptimizations?: MobileOptimizationConfig;
}

export interface MobileOptimizationConfig {
  enableBatteryOptimization: boolean;
  enableOfflineMode: boolean;
  reducedUpdateFrequency: boolean;
  compressionEnabled: boolean;
  prioritizeEssentialUpdates: boolean;
  adaptiveReconnection: boolean;
  backgroundSyncEnabled: boolean;
  networkAwareSync: boolean;
}

export interface NetworkState {
  isOnline: boolean;
  connectionType: "wifi" | "cellular" | "unknown";
  effectiveType: "2g" | "3g" | "4g" | "unknown";
  downlink: number;
  rtt: number;
}

export interface BatteryState {
  level: number;
  charging: boolean;
  chargingTime: number;
  dischargingTime: number;
}

interface OfflineQueue {
  operations: Operation[];
  maxSize: number;
  syncInProgress: boolean;
}

export interface MobileState {
  isLowPowerMode: boolean;
  isBackgroundMode: boolean;
  batteryState: BatteryState | null;
  networkState: NetworkState;
  lastSyncTime: number;
  pendingOperations: number;
  compressionRatio: number;
}

type SocketClientEventArgs<K extends keyof SocketClientEvents> = Parameters<
  SocketClientEvents[K]
>;

type SocketClientEventHandler<K extends keyof SocketClientEvents> = (
  ...args: SocketClientEventArgs<K>
) => void;

type SocketClientEventHandlers = {
  [K in keyof SocketClientEvents]: Set<SocketClientEventHandler<K>>;
};

type CustomSocketEventHandler<TPayload = unknown> = (payload: TPayload) => void;
type RegisteredCustomSocketEventHandler = Parameters<Socket["on"]>[1];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getTokenFromSocketAuth = (
  auth: Socket["auth"] | undefined,
): string | null => {
  if (!isRecord(auth)) {
    return null;
  }

  const token = auth.token;
  return typeof token === "string" ? token : null;
};

const normalizeError = (
  error: unknown,
  fallbackMessage = "Unknown error",
): Error => {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === "string") {
    return new Error(error);
  }

  if (isRecord(error) && typeof error.message === "string") {
    return new Error(error.message);
  }

  return new Error(fallbackMessage);
};

export interface SocketClientEvents {
  // Connection events
  connected: () => void;
  disconnected: (reason: string) => void;
  reconnecting: (attemptNumber: number) => void;
  reconnected: () => void;
  error: (error: Error) => void;

  // Project events
  projectJoined: (data: ProjectStateData) => void;
  projectLeft: () => void;
  projectStateReceived: (data: ProjectStateData) => void;

  // User events
  userJoined: (data: UserJoinedData) => void;
  userLeft: (data: UserLeftData) => void;

  // Collaboration events
  operationReceived: (data: CollaborationMessage) => void;
  operationAck: (data: OperationAckData) => void;
  cursorUpdate: (data: CursorMessage) => void;
  selectionUpdate: (data: SelectionMessage) => void;
  presenceUpdate: (data: PresenceMessage) => void;

  // Global presence
  globalPresence: (data: GlobalPresenceData) => void;

  // Session recovery events
  sessionRecoveryStarted: () => void;
  sessionRecoveryProgress: (progress: number, message: string) => void;
  sessionRecoveryCompleted: (missedOperationsCount: number) => void;
  sessionRecoveryFailed: (error: Error) => void;

  // Conflict auto-resolve events
  conflictAutoResolveStarted: (conflictId: string) => void;
  conflictAutoResolveCompleted: (result: ConflictAutoResolveResult) => void;
  conflictAutoResolveFailed: (conflictId: string, error: Error) => void;

  // Client capabilities events
  clientCapabilitiesNegotiated: (
    optimizations: CapabilityOptimizations,
  ) => void;
  clientCapabilitiesError: (error: Error) => void;

  // Batch operations events
  batchOperationCompleted: (result: BatchOperationResult) => void;
  batchOperationFailed: (batchId: string, error: Error) => void;

  // Automation rule refresh events
  "component-data-refresh": (data: { componentId: string }) => void;
}

const createSocketClientEventHandlers = (): SocketClientEventHandlers => ({
  connected: new Set(),
  disconnected: new Set(),
  reconnecting: new Set(),
  reconnected: new Set(),
  error: new Set(),
  projectJoined: new Set(),
  projectLeft: new Set(),
  projectStateReceived: new Set(),
  userJoined: new Set(),
  userLeft: new Set(),
  operationReceived: new Set(),
  operationAck: new Set(),
  cursorUpdate: new Set(),
  selectionUpdate: new Set(),
  presenceUpdate: new Set(),
  globalPresence: new Set(),
  sessionRecoveryStarted: new Set(),
  sessionRecoveryProgress: new Set(),
  sessionRecoveryCompleted: new Set(),
  sessionRecoveryFailed: new Set(),
  conflictAutoResolveStarted: new Set(),
  conflictAutoResolveCompleted: new Set(),
  conflictAutoResolveFailed: new Set(),
  clientCapabilitiesNegotiated: new Set(),
  clientCapabilitiesError: new Set(),
  batchOperationCompleted: new Set(),
  batchOperationFailed: new Set(),
  "component-data-refresh": new Set(),
});

export interface ProjectStateData {
  project: Project;
  components: Component[];
  session: {
    participants: Array<Record<string, unknown>>;
    activeUsers: string[];
  } | null;
  vectorClock: VectorClock;
  timestamp: number;
}

export interface UserJoinedData {
  userId: string;
  user: User;
  timestamp: number;
}

export interface UserLeftData {
  userId: string;
  timestamp: number;
}

export interface CollaborationMessage {
  type: "collaboration";
  payload: {
    operation: Operation;
    sessionId: string;
  };
  userId: string;
  projectId: string;
  timestamp: number;
}

export interface CursorMessage {
  type: "cursor";
  payload: {
    position: CursorPosition;
    sessionId: string;
  };
  userId: string;
  projectId: string;
  timestamp: number;
}

export interface SelectionMessage {
  type: "selection";
  payload: {
    selection: string[];
    sessionId: string;
  };
  userId: string;
  projectId: string;
  timestamp: number;
}

export interface PresenceMessage {
  type: "presence";
  payload: {
    action: string;
    user: {
      userId: string;
      socketId: string;
      lastSeenAt: Date;
    };
    sessionId: string;
  };
  userId: string;
  projectId: string;
  timestamp: number;
}

export interface GlobalPresenceData {
  userId: string;
  action: "connect" | "disconnect";
  timestamp: number;
}

export interface OperationAckData {
  operationId: string;
  transformedOperation: Operation;
  vectorClock: VectorClock;
}

// Session Recovery Types
export interface SessionRecoveryData {
  sessionId: string;
  lastKnownVectorClock: VectorClock;
  lastOperationId?: string;
  disconnectedAt: number;
  clientState?: {
    canvasState?: unknown;
    selectionState?: unknown;
    cursorPosition?: { x: number; y: number };
  };
}

export interface SessionRecoveryResponse {
  success: boolean;
  sessionId: string;
  missedOperations: Operation[];
  currentVectorClock: VectorClock;
  activeUsers: Array<{
    userId: string;
    cursor?: CursorPosition;
    selection?: string[];
  }>;
  conflictsDetected: VersionConflict[];
  recoveryDuration: number;
  message?: string;
  error?: string;
}

// Batch Operations Types
export interface BatchOperationData {
  operations: Operation[];
  batchId: string;
  projectId: string;
  userId: string;
  timestamp: number;
  metadata?: {
    source?: string;
    priority?: number;
    atomicExecution?: boolean;
  };
}

export interface BatchOperationResult {
  batchId: string;
  success: boolean;
  processedOperations: number;
  failedOperations: number;
  results: Array<{
    operationId: string;
    success: boolean;
    transformedOperation?: Operation;
    error?: string;
  }>;
  totalProcessingTime: number;
  conflictsDetected: VersionConflict[];
}

// Client Capabilities Types
export interface ClientCapabilities {
  deviceType: "desktop" | "tablet" | "mobile";
  inputMethod: "mouse" | "touch" | "pen" | "keyboard";
  screenSize: {
    width: number;
    height: number;
  };
  performance: {
    cpu: "high" | "medium" | "low";
    memory: "high" | "medium" | "low";
    network: "fast" | "moderate" | "slow";
  };
  features: {
    webGL: boolean;
    webRTC: boolean;
    serviceWorker: boolean;
    indexedDB: boolean;
    localStorage: boolean;
  };
  browser: {
    name: string;
    version: string;
    engine: string;
  };
  os: {
    name: string;
    version: string;
  };
}

export interface CapabilityOptimizations {
  enableHardwareAcceleration: boolean;
  maxOperationsPerBatch: number;
  operationThrottleMs: number;
  enableVideoCollaboration: boolean;
  enableAudioCollaboration: boolean;
  renderingQuality: "high" | "medium" | "low";
  enableOfflineMode: boolean;
  syncFrequency: number;
  compressionLevel: number;
}

// Conflict Auto-Resolve Types
export interface ConflictAutoResolveData {
  conflictId: string;
  conflict: VersionConflict;
  preferredStrategy?: "USE_SOURCE" | "USE_TARGET" | "MERGE" | "ESCALATE";
  userPreferences?: {
    preferNewest?: boolean;
    preferOwn?: boolean;
    preserveIntention?: boolean;
  };
  confidenceThreshold?: number;
}

export interface ConflictAutoResolveResult {
  conflictId: string;
  resolved: boolean;
  strategy: "USE_SOURCE" | "USE_TARGET" | "MERGE" | "ESCALATE";
  confidence: number;
  resolvedValue?: unknown;
  requiresManualReview: boolean;
  reasoning?: string;
  alternativeStrategies?: Array<{
    strategy: string;
    confidence: number;
    reasoning: string;
  }>;
}

export class SocketClient {
  private static instance: SocketClient | null = null;
  private socket: Socket | null = null;
  private eventHandlers: SocketClientEventHandlers =
    createSocketClientEventHandlers();
  private customEventHandlers = new Map<
    string,
    Set<RegisteredCustomSocketEventHandler>
  >();
  private connectionState:
    | "disconnected"
    | "connecting"
    | "connected"
    | "reconnecting" = "disconnected";
  private currentProjectId: string | null = null;
  private currentUserId: string | null = null;
  private isJoiningProject: boolean = false;
  private joiningProjectId: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private activeConnectionPromise: Promise<void> | null = null;
  private connectionOwners = new Map<string, SocketClientConfig>();
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private sessionHeartbeatInterval: NodeJS.Timeout | null = null;
  private lastActivity = Date.now();

  // Session recovery state tracking
  private sessionId: string | null = null;
  private lastKnownVectorClock: VectorClock = {};
  private lastOperationId: string | null = null;
  private disconnectedAt: number | null = null;
  private clientState: SessionRecoveryData["clientState"] | null = null;
  private isRecoveringSession = false;
  private sessionRecoveryAttempts = 0;
  private maxSessionRecoveryAttempts = 3;
  private recoveryTimeout: NodeJS.Timeout | null = null;

  // Client capabilities state tracking
  private clientCapabilities: ClientCapabilities | null = null;
  private negotiatedOptimizations: CapabilityOptimizations | null = null;
  private capabilitiesNegotiated = false;

  // Mobile optimization properties
  private mobileConfig: MobileOptimizationConfig;
  private mobileState: MobileState;
  private offlineQueue: OfflineQueue;
  private batteryMonitor: BatteryManager | null = null;
  private networkMonitor: NetworkInformation | null = null;
  private syncThrottleTimer: NodeJS.Timeout | null = null;
  private backgroundSyncTimer: NodeJS.Timeout | null = null;
  private operationBuffer: Operation[] = [];
  private lastBufferFlush = Date.now();
  private compressionWorker: Worker | null = null;

  private constructor() {
    // Initialize mobile optimization defaults
    this.mobileConfig = {
      enableBatteryOptimization: true,
      enableOfflineMode: true,
      reducedUpdateFrequency: false,
      compressionEnabled: true,
      prioritizeEssentialUpdates: true,
      adaptiveReconnection: true,
      backgroundSyncEnabled: true,
      networkAwareSync: true,
    };

    this.mobileState = {
      isLowPowerMode: false,
      isBackgroundMode: false,
      batteryState: null,
      networkState: {
        isOnline: navigator.onLine,
        connectionType: "unknown",
        effectiveType: "unknown",
        downlink: 0,
        rtt: 0,
      },
      lastSyncTime: Date.now(),
      pendingOperations: 0,
      compressionRatio: 1,
    };

    this.offlineQueue = {
      operations: [],
      maxSize: 1000,
      syncInProgress: false,
    };

    this.initializeMobileOptimizations();
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): SocketClient {
    if (!SocketClient.instance) {
      SocketClient.instance = new SocketClient();
    }
    return SocketClient.instance;
  }

  /**
   * Initialize mobile-specific optimizations
   */
  private initializeMobileOptimizations(): void {
    // Initialize battery monitoring
    this.initializeBatteryMonitoring();

    // Initialize network monitoring
    this.initializeNetworkMonitoring();

    // Initialize background mode detection
    this.initializeBackgroundModeDetection();

    // Initialize compression worker if supported
    this.initializeCompressionWorker();

    // Set up adaptive sync scheduling
    this.setupAdaptiveSync();
  }

  private resolveSocketEndpoint(rawUrl: string): {
    origin: string;
    path: string;
    protocol: string;
  } {
    const resolvedUrl = new URL(rawUrl, window.location.origin);
    const pathPrefix =
      resolvedUrl.pathname === "/"
        ? ""
        : resolvedUrl.pathname.replace(/\/$/, "");

    return {
      origin: resolvedUrl.origin,
      path: pathPrefix ? `${pathPrefix}/socket.io` : "/socket.io",
      protocol: resolvedUrl.protocol,
    };
  }

  private resetFailedConnection(): void {
    this.disconnect({ clearOwners: false });
    this.currentUserId = null;
  }

  private usesDifferentAuth(config: SocketClientConfig): boolean {
    return (
      this.currentUserId !== config.userId ||
      this.getAuthToken() !== config.token
    );
  }

  public async acquireConnection(
    ownerId: string,
    config: SocketClientConfig,
  ): Promise<void> {
    if (!ownerId) {
      throw new Error("Socket connection owner ID is required");
    }

    this.connectionOwners.set(ownerId, config);

    if (this.socket?.connected && this.usesDifferentAuth(config)) {
      logger.info("Reconnecting shared socket with updated auth", {
        ownerId,
        userId: config.userId,
      });
      this.disconnect({ clearOwners: false });
    }

    await this.connect(config);
  }

  public releaseConnection(ownerId: string): void {
    this.connectionOwners.delete(ownerId);

    if (this.connectionOwners.size > 0) {
      logger.debug("Retaining shared socket connection for active owners", {
        ownerId,
        remainingOwners: this.connectionOwners.size,
      });
      return;
    }

    this.disconnect({ clearOwners: false });
  }

  /**
   * Initialize and connect to WebSocket server with mobile optimizations
   */
  public async connect(config: SocketClientConfig): Promise<void> {
    if (this.socket?.connected) {
      logger.warn(
        "🔒 Socket already connected, preventing duplicate connection",
        {
          userId: config.userId,
          currentUserId: this.currentUserId,
          connectionState: this.connectionState,
        },
      );
      return;
    }

    // Additional StrictMode protection - reuse the in-flight connect attempt
    if (this.connectionState === "connecting" && this.activeConnectionPromise) {
      logger.debug(
        "Connection already in progress; reusing active connection attempt",
        {
          userId: config.userId,
          currentUserId: this.currentUserId,
        },
      );
      return this.activeConnectionPromise;
    }

    this.connectionState = "connecting";
    this.currentUserId = config.userId;

    // Apply mobile configuration if provided
    if (config.mobileOptimizations) {
      this.mobileConfig = {
        ...this.mobileConfig,
        ...config.mobileOptimizations,
      };
    }

    // SECURITY: Enforce secure WebSocket connections in production
    const isProduction = window.location.protocol === "https:";
    const secureUrl = isProduction
      ? config.url.replace(/^http:/, "https:").replace(/^ws:/, "wss:")
      : config.url;
    const resolvedEndpoint = this.resolveSocketEndpoint(secureUrl);

    if (
      isProduction &&
      !["wss:", "https:"].includes(resolvedEndpoint.protocol)
    ) {
      throw new Error(
        "Secure WebSocket connections (WSS) are required in production",
      );
    }

    // Adjust connection parameters based on mobile state
    const mobileOptimizedConfig = this.getMobileOptimizedConfig(config);

    try {
      if (this.socket) {
        this.detachCustomEventHandlers(this.socket);
        this.socket.disconnect();
        this.socket = null;
      }

      this.socket = io(resolvedEndpoint.origin, {
        auth: {
          token: config.token,
          userId: config.userId,
          deviceType: this.getDeviceType(),
          capabilities: this.getClientCapabilities(),
        },
        path: resolvedEndpoint.path,
        transports: this.getOptimalTransports(),
        timeout: mobileOptimizedConfig.timeout,
        forceNew: true,
        reconnection: true,
        reconnectionAttempts: mobileOptimizedConfig.reconnectionAttempts,
        reconnectionDelay: mobileOptimizedConfig.reconnectionDelay,
        reconnectionDelayMax: mobileOptimizedConfig.reconnectionDelayMax,
        // SECURITY: Enhanced security options
        secure: isProduction,
        rejectUnauthorized: isProduction, // Validate SSL certificates in production
        // Mobile-specific optimizations
      });

      this.setupEventListeners();
      this.startHeartbeat();
      this.startMobileOptimizations();

      const connectionPromise = new Promise<void>((resolve, reject) => {
        const socket = this.socket;

        if (!socket) {
          reject(new Error("Socket initialization failed"));
          return;
        }

        let timeout: ReturnType<typeof setTimeout>;
        const cleanupInitialConnectHandlers = () => {
          clearTimeout(timeout);
          socket.off("connect", onInitialConnect);
          socket.off("connect_error", onInitialConnectError);
        };

        const onInitialConnect = () => {
          cleanupInitialConnectHandlers();
          this.connectionState = "connected";
          this.reconnectAttempts = 0;
          logger.info("Socket connected successfully");

          // Trigger haptic feedback for successful connection on mobile
          if (this.isMobileDevice()) {
            hapticFeedbackService.trigger("sync-complete");
          }

          // Process offline queue if enabled
          if (this.mobileConfig.enableOfflineMode) {
            this.processOfflineQueue();
          }

          resolve();
        };

        const onInitialConnectError = (error: Error) => {
          cleanupInitialConnectHandlers();
          this.resetFailedConnection();
          logger.error("Socket connection failed:", error);
          reject(error);
        };

        timeout = setTimeout(() => {
          cleanupInitialConnectHandlers();
          this.resetFailedConnection();
          reject(new Error("Connection timeout"));
        }, config.timeout || 20000);

        socket.on("connect", onInitialConnect);
        socket.on("connect_error", onInitialConnectError);
      });

      const trackedConnectionPromise = connectionPromise.finally(() => {
        if (this.activeConnectionPromise === trackedConnectionPromise) {
          this.activeConnectionPromise = null;
        }
      });

      this.activeConnectionPromise = trackedConnectionPromise;
      return trackedConnectionPromise;
    } catch (error) {
      this.activeConnectionPromise = null;
      this.resetFailedConnection();
      logger.error("Failed to initialize socket:", error);
      throw error;
    }
  }

  /**
   * Disconnect from WebSocket server with mobile cleanup
   */
  public disconnect(options: { clearOwners?: boolean } = {}): void {
    const { clearOwners = true } = options;

    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    // Stop session heartbeat
    this.stopSessionHeartbeat();

    // Clean up mobile-specific timers
    if (this.syncThrottleTimer) {
      clearTimeout(this.syncThrottleTimer);
      this.syncThrottleTimer = null;
    }

    if (this.backgroundSyncTimer) {
      clearInterval(this.backgroundSyncTimer);
      this.backgroundSyncTimer = null;
    }

    // Clean up monitors
    this.cleanupMobileMonitors();

    // Clean up compression worker
    if (this.compressionWorker) {
      this.compressionWorker.terminate();
      this.compressionWorker = null;
    }

    if (this.socket) {
      this.detachCustomEventHandlers(this.socket);
      this.socket.disconnect();
      this.socket = null;
    }

    this.connectionState = "disconnected";
    this.currentUserId = null;
    this.currentProjectId = null;
    this.isJoiningProject = false;
    this.joiningProjectId = null;
    this.activeConnectionPromise = null;

    // Clear session recovery state to prevent stale data on reconnect
    this.sessionId = null;
    this.lastKnownVectorClock = {};
    this.lastOperationId = null;
    this.disconnectedAt = null;
    this.clientState = null;
    this.isRecoveringSession = false;
    this.sessionRecoveryAttempts = 0;

    if (clearOwners) {
      this.connectionOwners.clear();
    }

    logger.info("Socket disconnected");
  }

  /**
   * Check if socket is connected
   */
  public isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get connection state
   */
  public getConnectionState(): string {
    return this.connectionState;
  }

  /**
   * Get current auth token used by the socket
   * Used to detect when token has changed and reconnection is needed
   */
  public getAuthToken(): string | null {
    return getTokenFromSocketAuth(this.socket?.auth);
  }

  /**
   * Join project room
   */
  public async joinProject(projectId: string): Promise<void> {
    if (!this.socket?.connected) {
      throw new Error("Socket not connected");
    }

    if (this.currentProjectId === projectId) {
      logger.warn("Already in project:", projectId);
      return;
    }

    // Prevent concurrent join attempts globally
    if (this.isJoiningProject) {
      if (this.joiningProjectId === projectId) {
        logger.warn(
          "Already joining this project, waiting for completion:",
          projectId,
        );
        // Wait for the current join to complete
        return new Promise((resolve, reject) => {
          const checkInterval = setInterval(() => {
            if (!this.isJoiningProject) {
              clearInterval(checkInterval);
              if (this.currentProjectId === projectId) {
                resolve();
              } else {
                reject(new Error("Previous join operation failed"));
              }
            }
          }, 100);

          // Timeout after 20 seconds
          setTimeout(() => {
            clearInterval(checkInterval);
            reject(new Error("Waiting for previous join timed out"));
          }, 20000);
        });
      } else {
        throw new Error(
          `Already joining different project: ${this.joiningProjectId}`,
        );
      }
    }

    this.isJoiningProject = true;
    this.joiningProjectId = projectId;

    // Leave the existing project first to avoid conflicts
    if (this.currentProjectId) {
      await this.leaveProject();
    }

    return new Promise((resolve, reject) => {
      const socket = this.socket;
      if (!socket) {
        this.isJoiningProject = false;
        this.joiningProjectId = null;
        reject(new Error("Socket not connected"));
        return;
      }

      const cleanup = () => {
        socket.off("project-state", onProjectState);
        socket.off("error", onError);
        this.isJoiningProject = false;
        this.joiningProjectId = null;
      };

      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error("Join project timeout"));
      }, 15000); // Increased timeout to 15 seconds

      const onProjectState = (data: ProjectStateData) => {
        clearTimeout(timeout);
        this.currentProjectId = projectId;
        this.emit("projectJoined", data);
        cleanup();
        resolve();
      };

      const onError = (error: unknown) => {
        if (
          typeof error === "object" &&
          error !== null &&
          "type" in error &&
          error.type === "join-project-failed"
        ) {
          clearTimeout(timeout);
          cleanup();
          const message =
            "message" in error && typeof error.message === "string"
              ? error.message
              : "Failed to join project";
          reject(new Error(message));
        }
      };

      socket.on("project-state", onProjectState);
      socket.on("error", onError);

      socket.emit("join-project", { projectId });
    });
  }

  /**
   * Leave project room
   */
  public async leaveProject(): Promise<void> {
    if (!this.socket?.connected || !this.currentProjectId) {
      return;
    }

    this.socket.emit("leave-project", { projectId: this.currentProjectId });
    this.currentProjectId = null;
    this.isJoiningProject = false;
    this.joiningProjectId = null;
    this.emit("projectLeft");
  }

  /**
   * Send operation to server
   */
  public sendOperation(operation: Operation): void {
    if (!this.socket?.connected || !this.currentProjectId) {
      throw new Error("Not connected to project");
    }

    // Track the operation ID for session recovery
    this.lastOperationId = operation.id;

    // Update vector clock if present
    if (operation.vector) {
      this.lastKnownVectorClock = { ...operation.vector };
    }

    this.socket.emit("operation", {
      type: "collaboration",
      payload: {
        operation,
        sessionId: this.sessionId || this.currentProjectId,
      },
      userId: operation.userId,
      projectId: this.currentProjectId,
      timestamp: Date.now(),
    });

    this.updateActivity();
  }

  /**
   * Send cursor update
   */
  public sendCursorUpdate(position: CursorPosition, throttle = true): void {
    if (
      !this.socket?.connected ||
      !this.currentProjectId ||
      !this.currentUserId
    ) {
      return;
    }

    if (throttle) {
      this.throttledCursorUpdate(position);
    } else {
      this.socket.emit("cursor-update", {
        type: "cursor",
        payload: {
          position,
          sessionId: this.currentProjectId,
        },
        userId: this.currentUserId,
        projectId: this.currentProjectId,
        timestamp: Date.now(),
      });
    }

    this.updateActivity();
  }

  private throttledCursorUpdate = this.throttle((position: CursorPosition) => {
    if (
      !this.socket?.connected ||
      !this.currentProjectId ||
      !this.currentUserId
    ) {
      return;
    }

    this.socket.emit("cursor-update", {
      type: "cursor",
      payload: {
        position,
        sessionId: this.currentProjectId,
      },
      userId: this.currentUserId,
      projectId: this.currentProjectId,
      timestamp: Date.now(),
    });
  }, 60); // 60ms throttling for optimal performance

  /**
   * Send selection update
   */
  public sendSelectionUpdate(selection: string[]): void {
    if (
      !this.socket?.connected ||
      !this.currentProjectId ||
      !this.currentUserId
    ) {
      return;
    }

    this.socket.emit("selection-update", {
      type: "selection",
      payload: {
        selection,
        sessionId: this.currentProjectId,
      },
      userId: this.currentUserId,
      projectId: this.currentProjectId,
      timestamp: Date.now(),
    });

    this.updateActivity();
  }

  /**
   * Send presence update
   */
  public sendPresenceUpdate(action: string): void {
    if (
      !this.socket?.connected ||
      !this.currentProjectId ||
      !this.currentUserId
    ) {
      return;
    }

    this.socket.emit("presence-update", {
      type: "presence",
      payload: {
        action,
      },
      userId: this.currentUserId,
      projectId: this.currentProjectId,
      timestamp: Date.now(),
    });

    this.updateActivity();
  }

  /**
   * Request full sync
   */
  public requestSync(): void {
    if (!this.socket?.connected || !this.currentProjectId) {
      throw new Error("Not connected to project");
    }

    this.socket.emit("request-sync", {
      projectId: this.currentProjectId,
      timestamp: Date.now(),
    });
  }

  /**
   * Register event handler
   */
  public on<K extends keyof SocketClientEvents>(
    event: K,
    handler: SocketClientEventHandler<K>,
  ): () => void {
    this.eventHandlers[event].add(handler);
    return () => {
      this.off(event, handler);
    };
  }

  /**
   * Unregister event handler
   */
  public off<K extends keyof SocketClientEvents>(
    event: K,
    handler?: SocketClientEventHandler<K>,
  ): void {
    if (handler) {
      this.eventHandlers[event].delete(handler);
    } else {
      this.eventHandlers[event].clear();
    }
  }

  /**
   * Register handler for custom events not in SocketClientEvents
   * Useful for domain-specific events like invitations, notifications, etc.
   */
  public onCustomEvent<TPayload = unknown>(
    event: string,
    handler: CustomSocketEventHandler<TPayload>,
  ): () => void {
    const handlers = this.getOrCreateCustomEventHandlers(event);
    const isRegistered = handlers.has(handler);

    handlers.add(handler);

    if (this.socket && !isRegistered) {
      this.socket.on(event, handler);
    }

    return () => {
      this.offCustomEvent(event, handler);
    };
  }

  public emitCustomEvent(event: string, data: unknown): boolean {
    if (!this.socket) {
      logger.warn("Cannot emit custom socket event: socket is not initialized", {
        event,
      });
      return false;
    }

    this.socket.emit(event, data);
    return true;
  }

  /**
   * Unregister custom event handler
   */
  public offCustomEvent<TPayload = unknown>(
    event: string,
    handler?: CustomSocketEventHandler<TPayload>,
  ): void {
    const handlers = this.customEventHandlers.get(event);

    if (!handlers) {
      return;
    }

    if (this.socket) {
      if (handler) {
        this.socket.off(event, handler);
      } else {
        for (const registeredHandler of handlers) {
          this.socket.off(event, registeredHandler);
        }
      }
    }

    if (handler) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.customEventHandlers.delete(event);
      }
      return;
    }

    this.customEventHandlers.delete(event);
  }

  private getOrCreateCustomEventHandlers(
    event: string,
  ): Set<RegisteredCustomSocketEventHandler> {
    const handlers = this.customEventHandlers.get(event);

    if (handlers) {
      return handlers;
    }

    const newHandlers = new Set<RegisteredCustomSocketEventHandler>();
    this.customEventHandlers.set(event, newHandlers);
    return newHandlers;
  }

  private bindCustomEventHandlers(socket: Socket): void {
    for (const [event, handlers] of this.customEventHandlers) {
      for (const handler of handlers) {
        socket.off(event, handler);
        socket.on(event, handler);
      }
    }
  }

  private detachCustomEventHandlers(socket: Socket): void {
    for (const [event, handlers] of this.customEventHandlers) {
      for (const handler of handlers) {
        socket.off(event, handler);
      }
    }
  }

  /**
   * Emit event to registered handlers
   */
  private emit<K extends keyof SocketClientEvents>(
    event: K,
    ...args: SocketClientEventArgs<K>
  ): void {
    const handlers = Array.from(this.eventHandlers[event]);

    for (const handler of handlers) {
      try {
        handler.apply(undefined, args);
      } catch (error) {
        logger.error(`Error in event handler for ${event}:`, error);
      }
    }
  }

  /**
   * Setup socket event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    this.bindCustomEventHandlers(this.socket);

    // Connection events
    this.socket.on("connect", () => {
      this.connectionState = "connected";
      this.emit("connected");
      logger.info("Socket connected");
    });

    this.socket.on("disconnect", (reason) => {
      this.connectionState = "disconnected";
      this.disconnectedAt = Date.now();

      // Store current client state for recovery
      this.storeClientStateForRecovery();

      this.emit("disconnected", reason);
      logger.warn("Socket disconnected:", reason);
    });

    this.socket.on("reconnect", () => {
      this.connectionState = "connected";

      // Check if we need session recovery
      if (this.shouldAttemptSessionRecovery()) {
        this.performSessionRecovery();
      } else {
        this.emit("reconnected");
        logger.info("Socket reconnected without recovery");
      }
    });

    this.socket.on("reconnect_attempt", (attemptNumber) => {
      this.connectionState = "reconnecting";
      this.reconnectAttempts = attemptNumber;
      this.emit("reconnecting", attemptNumber);
      logger.info(`Socket reconnection attempt ${attemptNumber}`);
    });

    this.socket.on("connect_error", (error) => {
      this.emit("error", error);
      logger.error("Socket connection error:", error);
    });

    // Project events
    this.socket.on("project-state", (data: ProjectStateData) => {
      // Update session ID and vector clock from project state
      if (data.session) {
        this.sessionId = data.project?.id || this.currentProjectId;
      }
      if (data.vectorClock) {
        this.lastKnownVectorClock = { ...data.vectorClock };
      }
      this.emit("projectStateReceived", data);
    });

    this.socket.on("user-joined", (data: UserJoinedData) => {
      this.emit("userJoined", data);
    });

    this.socket.on("user-left", (data: UserLeftData) => {
      this.emit("userLeft", data);
    });

    // Collaboration events
    this.socket.on("operation", (data: CollaborationMessage) => {
      // Update last known operation ID and vector clock
      if (data.payload?.operation) {
        const op = data.payload.operation;
        if (op.id) {
          this.lastOperationId = op.id;
        }
        if (op.vector) {
          this.lastKnownVectorClock = { ...op.vector };
        }
      }
      this.emit("operationReceived", data);
    });

    this.socket.on("operation-ack", (data: OperationAckData) => {
      // Update vector clock from acknowledgment
      if (data.vectorClock) {
        this.lastKnownVectorClock = { ...data.vectorClock };
      }
      this.emit("operationAck", data);
    });

    // Session recovery events
    this.socket.on(
      "session-recovery-response",
      (response: SessionRecoveryResponse) => {
        this.handleSessionRecoveryResponse(response);
      },
    );

    // Conflict auto-resolve events
    this.socket.on(
      "conflict-auto-resolve-response",
      (result: ConflictAutoResolveResult) => {
        this.handleConflictAutoResolveResponse(result);
      },
    );

    // Client capabilities events
    this.socket.on(
      "client-capabilities-response",
      (optimizations: CapabilityOptimizations) => {
        this.handleClientCapabilitiesResponse(optimizations);
      },
    );

    // Batch operations events
    this.socket.on(
      "batch-operations-result",
      (result: BatchOperationResult) => {
        this.handleBatchOperationResult(result);
      },
    );

    this.socket.on("cursor-update", (data: CursorMessage) => {
      this.emit("cursorUpdate", data);
    });

    this.socket.on("selection-update", (data: SelectionMessage) => {
      this.emit("selectionUpdate", data);
    });

    this.socket.on("presence-update", (data: PresenceMessage) => {
      this.emit("presenceUpdate", data);
    });

    this.socket.on("global-presence", (data: GlobalPresenceData) => {
      this.emit("globalPresence", data);
    });

    // Error handling
    this.socket.on("error", (error: unknown) => {
      logger.error("Socket error:", error);
      const message =
        typeof error === "object" &&
        error !== null &&
        "message" in error &&
        typeof error.message === "string"
          ? error.message
          : "Unknown socket error";
      this.emit("error", new Error(message));
    });
  }

  /**
   * Start heartbeat to maintain connection
   */
  private startHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    this.heartbeatInterval = setInterval(() => {
      if (this.socket?.connected) {
        this.socket.emit("ping");

        // Check for inactivity
        const inactiveTime = Date.now() - this.lastActivity;
        if (inactiveTime > 5 * 60 * 1000) {
          // 5 minutes
          logger.info("Sending presence update due to inactivity");
          this.sendPresenceUpdate("idle");
        }
      }
    }, 25000); // Every 25 seconds

    // Also start session heartbeat
    this.startSessionHeartbeat();
  }

  /**
   * Start session heartbeat - sends periodic heartbeat to keep session alive
   * This prevents session timeout for long-running dashboards
   */
  public startSessionHeartbeat(): void {
    // Stop the existing interval
    if (this.sessionHeartbeatInterval) {
      clearInterval(this.sessionHeartbeatInterval);
    }

    // Send heartbeat every 5 minutes to keep session alive
    this.sessionHeartbeatInterval = setInterval(
      () => {
        if (this.socket?.connected) {
          this.socket.emit("session-heartbeat", { timestamp: Date.now() });
          logger.debug("Session heartbeat sent");
        }
      },
      5 * 60 * 1000,
    ); // Every 5 minutes

    // Set up listener for heartbeat acknowledgment
    this.socket?.on(
      "session-heartbeat-ack",
      (data: {
        timestamp: number;
        sessionExpiresAt: number;
        latency: number;
      }) => {
        logger.debug("Session heartbeat acknowledged", {
          sessionExpiresAt: new Date(data.sessionExpiresAt).toISOString(),
          latency: data.latency,
        });

        // Optionally update auth store with new session expiry
        void import("@/stores/auth/auth-store")
          .then(({ useAuthStore }) => {
            const state = useAuthStore.getState();
            if (state.tokens) {
              // Note: We don't update tokens here, just track that session is alive
              logger.debug(
                "Session still active, expires at:",
                new Date(data.sessionExpiresAt).toISOString(),
              );
            }
          })
          .catch((error: unknown) => {
            logger.error(
              "Failed to inspect auth store during session heartbeat:",
              error,
            );
          });
      },
    );

    // Send initial heartbeat immediately
    if (this.socket?.connected) {
      this.socket.emit("session-heartbeat", { timestamp: Date.now() });
    }
  }

  /**
   * Stop session heartbeat
   */
  public stopSessionHeartbeat(): void {
    if (this.sessionHeartbeatInterval) {
      clearInterval(this.sessionHeartbeatInterval);
      this.sessionHeartbeatInterval = null;
    }
  }

  /**
   * Refresh tokens via WebSocket connection
   * This is faster than HTTP refresh and keeps the session alive
   * @returns Promise resolving to new tokens or null if refresh failed
   */
  public async refreshTokenViaWebSocket(): Promise<{
    accessToken: string;
    refreshToken: string;
    expiresAt: number;
    tokenType: string;
  } | null> {
    return new Promise((resolve) => {
      if (!this.socket?.connected) {
        logger.warn("Cannot refresh token via WebSocket - not connected");
        resolve(null);
        return;
      }

      // Set timeout for response
      const timeout = setTimeout(() => {
        logger.warn("Token refresh via WebSocket timed out");
        resolve(null);
      }, 10000); // 10 second timeout

      // Listen for response (one-time)
      this.socket.once(
        "token-refresh-response",
        (data: {
          success: boolean;
          tokens?: {
            accessToken: string;
            refreshToken: string;
            expiresAt: number;
            tokenType: string;
          };
          error?: string;
        }) => {
          clearTimeout(timeout);

          if (data.success && data.tokens) {
            logger.info("Token refreshed via WebSocket");
            const tokens = data.tokens;

            // Update auth store with new tokens
            void import("@/stores/auth/auth-store")
              .then(({ useAuthStore }) => {
                useAuthStore.getState().setTokens({
                  accessToken: tokens.accessToken,
                  refreshToken: tokens.refreshToken,
                  expiresAt: tokens.expiresAt,
                  tokenType: tokens.tokenType,
                });
              })
              .catch((error: unknown) => {
                logger.error(
                  "Failed to update auth store after token refresh:",
                  error,
                );
              });

            resolve(tokens);
          } else {
            logger.warn("Token refresh via WebSocket failed:", data.error);
            resolve(null);
          }
        },
      );

      // Send refresh request
      this.socket.emit("token-refresh-request");
      logger.debug("Token refresh request sent via WebSocket");
    });
  }

  /**
   * Update last activity timestamp
   */
  private updateActivity(): void {
    this.lastActivity = Date.now();
  }

  /**
   * Throttle utility function
   */
  private throttle<TArgs extends unknown[]>(
    func: (...args: TArgs) => void,
    delay: number,
  ): (...args: TArgs) => void {
    let timeoutId: NodeJS.Timeout | null = null;
    let lastExecTime = 0;

    return (...args: TArgs) => {
      const currentTime = Date.now();

      if (currentTime - lastExecTime > delay) {
        func.apply(undefined, args);
        lastExecTime = currentTime;
      } else {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(
          () => {
            func.apply(undefined, args);
            lastExecTime = Date.now();
          },
          delay - (currentTime - lastExecTime),
        );
      }
    };
  }

  /**
   * Get current project ID
   */
  public getCurrentProjectId(): string | null {
    return this.currentProjectId;
  }

  /**
   * Get reconnection status
   */
  public getReconnectionStatus(): { attempts: number; maxAttempts: number } {
    return {
      attempts: this.reconnectAttempts,
      maxAttempts: this.maxReconnectAttempts,
    };
  }

  /**
   * Check if client is currently in a project
   */
  public isInProject(): boolean {
    return this.currentProjectId !== null;
  }

  /**
   * Get connection statistics
   */
  public getStats(): {
    connected: boolean;
    connectionState: string;
    currentProject: string | null;
    lastActivity: number;
    reconnectAttempts: number;
  } {
    return {
      connected: this.isConnected(),
      connectionState: this.connectionState,
      currentProject: this.currentProjectId,
      lastActivity: this.lastActivity,
      reconnectAttempts: this.reconnectAttempts,
    };
  }

  // Mobile optimization helper methods

  private initializeBatteryMonitoring(): void {
    if (navigator.getBattery) {
      navigator
        .getBattery()
        .then((battery) => {
          this.batteryMonitor = battery;
          this.mobileState.batteryState = {
            level: battery.level,
            charging: battery.charging,
            chargingTime: battery.chargingTime,
            dischargingTime: battery.dischargingTime,
          };

          this.updatePowerMode();

          battery.addEventListener("levelchange", () => {
            if (this.mobileState.batteryState) {
              this.mobileState.batteryState.level = battery.level;
              this.updatePowerMode();
            }
          });
        })
        .catch(() => {
          // Battery API not available - silently ignore
        });
    }
  }

  private isMobileDevice(): boolean {
    return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
  }

  private updatePowerMode(): void {
    const battery = this.mobileState.batteryState;
    if (!battery) return;

    this.mobileState.isLowPowerMode = !battery.charging && battery.level < 0.2;
  }

  private shouldSkipNonEssentialUpdate(): boolean {
    return (
      (this.mobileState.isLowPowerMode &&
        this.mobileConfig.enableBatteryOptimization) ||
      this.mobileState.isBackgroundMode ||
      this.mobileState.networkState.effectiveType === "2g"
    );
  }

  private getMobileAwareThrottleDelay(): number {
    if (this.mobileState.isLowPowerMode) return 200;
    if (this.mobileState.networkState.effectiveType === "2g") return 500;
    return 60;
  }

  private getMobileAwareHeartbeatInterval(): number {
    if (this.mobileState.isLowPowerMode) return 60000;
    if (this.mobileState.isBackgroundMode) return 120000;
    return 25000;
  }

  private getMobileAwareInactivityThreshold(): number {
    if (this.mobileState.isLowPowerMode) return 10 * 60 * 1000;
    return 5 * 60 * 1000;
  }

  private queueOfflineOperation(operation: Operation): void {
    if (this.offlineQueue.operations.length >= this.offlineQueue.maxSize) {
      this.offlineQueue.operations.shift();
    }
    this.offlineQueue.operations.push(operation);
  }

  private async replayOfflineOperationSequence(
    operations: Operation[],
    index = 0,
  ): Promise<void> {
    if (index >= operations.length) {
      return;
    }

    const operation = operations[index];
    if (operation === undefined) {
      return;
    }

    if (!this.socket?.connected) {
      this.offlineQueue.operations.unshift(operation);
      return;
    }

    this.sendOperation(operation);
    // Preserve causal order and throttle replay so reconnects do not flood the server.
    await new Promise((resolve) => setTimeout(resolve, 50));
    return this.replayOfflineOperationSequence(operations, index + 1);
  }

  private async processOfflineQueue(): Promise<void> {
    if (
      this.offlineQueue.syncInProgress ||
      this.offlineQueue.operations.length === 0
    ) {
      return;
    }

    this.offlineQueue.syncInProgress = true;

    try {
      const operations = [...this.offlineQueue.operations];
      this.offlineQueue.operations = [];

      await this.replayOfflineOperationSequence(operations);

      if (this.isMobileDevice()) {
        hapticFeedbackService.trigger("sync-complete");
      }
    } finally {
      this.offlineQueue.syncInProgress = false;
    }
  }

  private bufferOperation(operation: Operation): void {
    this.operationBuffer.push(operation);

    const bufferAge = Date.now() - this.lastBufferFlush;
    if (this.operationBuffer.length >= 5 || bufferAge > 2000) {
      this.flushOperationBuffer();
    }
  }

  private flushOperationBuffer(): void {
    if (this.operationBuffer.length === 0) return;

    const operations = [...this.operationBuffer];
    this.operationBuffer = [];
    this.lastBufferFlush = Date.now();

    if (this.socket?.connected && this.currentProjectId && this.currentUserId) {
      // Generate unique batch ID
      const batchId = `batch_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

      const batchData = {
        operations,
        batchId,
        projectId: this.currentProjectId,
        userId: this.currentUserId,
        timestamp: Date.now(),
        metadata: {
          source: "operation-buffer",
          priority: this.getBatchPriority(operations),
          atomicExecution: this.shouldUseAtomicExecution(operations),
        },
      };

      logger.debug("Sending batch operations", {
        batchId,
        operationCount: operations.length,
        priority: batchData.metadata.priority,
        atomicExecution: batchData.metadata.atomicExecution,
      });

      this.socket.emit("batch-operations", batchData);
    }
  }

  /**
   * Determine batch priority based on operation types
   */
  private getBatchPriority(operations: Operation[]): number {
    const priorities = operations.map((op) => {
      if (
        op.type === OperationType.DELETE ||
        op.type === OperationType.CREATE_COMPONENT
      )
        return 3; // high
      if (op.type === OperationType.UPDATE) return 2; // medium
      return 1; // low
    });
    return priorities.reduce<number>(
      (highestPriority, priority) => Math.max(highestPriority, priority),
      1,
    );
  }

  /**
   * Determine if batch should use atomic execution
   */
  private shouldUseAtomicExecution(operations: Operation[]): boolean {
    // Use atomic execution for critical operations or related operations
    const hasDeleteOrCreate = operations.some(
      (op) =>
        op.type === OperationType.DELETE ||
        op.type === OperationType.CREATE_COMPONENT,
    );

    // Also use atomic execution if operations affect the same component
    const componentIds = new Set(operations.map((op) => op.componentId));
    const affectsSameComponent =
      componentIds.size === 1 && operations.length > 1;

    return hasDeleteOrCreate || affectsSameComponent;
  }

  private startOperationBuffering(): void {
    setInterval(() => {
      if (this.operationBuffer.length > 0) {
        this.flushOperationBuffer();
      }
    }, 1000);
  }

  private compressOperation(operation: Operation): Operation {
    return operation; // Placeholder for compression
  }

  private getOperationPriority(
    operation: Operation,
  ): "high" | "medium" | "low" {
    if (
      operation.type === OperationType.DELETE ||
      operation.type === OperationType.CREATE_COMPONENT
    ) {
      return "high";
    }
    return "medium";
  }

  private getDeviceType(): string {
    if (/Mobi|Android/i.test(navigator.userAgent)) return "mobile";
    if (/Tablet|iPad/i.test(navigator.userAgent)) return "tablet";
    return "desktop";
  }

  private detectClientCapabilities(): ClientCapabilities {
    return {
      deviceType: this.detectDeviceType(),
      inputMethod: this.detectInputMethod(),
      screenSize: {
        width: window.screen.width,
        height: window.screen.height,
      },
      performance: {
        cpu: this.detectCpuPerformance(),
        memory: this.detectMemoryPerformance(),
        network: this.detectNetworkPerformance(),
      },
      features: {
        webGL: this.detectWebGLSupport(),
        webRTC: this.detectWebRTCSupport(),
        serviceWorker: this.detectServiceWorkerSupport(),
        indexedDB: this.detectIndexedDBSupport(),
        localStorage: this.detectLocalStorageSupport(),
      },
      browser: this.detectBrowserInfo(),
      os: this.detectOSInfo(),
    };
  }

  private detectDeviceType(): "desktop" | "tablet" | "mobile" {
    const ua = navigator.userAgent;
    if (/Mobi|Android/i.test(ua)) return "mobile";
    if (/Tablet|iPad/i.test(ua)) return "tablet";
    return "desktop";
  }

  private detectInputMethod(): "mouse" | "touch" | "pen" | "keyboard" {
    if ("ontouchstart" in window && navigator.maxTouchPoints > 0) {
      if (
        navigator.maxTouchPoints === 1 &&
        /stylus/i.test(navigator.userAgent)
      ) {
        return "pen";
      }
      return "touch";
    }
    return "mouse";
  }

  private detectCpuPerformance(): "high" | "medium" | "low" {
    const cores = navigator.hardwareConcurrency || 1;
    if (cores >= 8) return "high";
    if (cores >= 4) return "medium";
    return "low";
  }

  private detectMemoryPerformance(): "high" | "medium" | "low" {
    const memory = navigator.deviceMemory ?? 0;
    if (memory >= 8) return "high";
    if (memory >= 4) return "medium";
    return "low";
  }

  private detectNetworkPerformance(): "fast" | "moderate" | "slow" {
    const connection = navigator.connection || navigator.mozConnection;
    this.networkMonitor = connection ?? null;
    if (connection) {
      if (connection.effectiveType === "4g") return "fast";
      if (connection.effectiveType === "3g") return "moderate";
      return "slow";
    }
    // Fallback based on device type
    return this.detectDeviceType() === "mobile" ? "moderate" : "fast";
  }

  private detectWebGLSupport(): boolean {
    try {
      const canvas = document.createElement("canvas");
      return !!(
        canvas.getContext("webgl") || canvas.getContext("experimental-webgl")
      );
    } catch {
      return false;
    }
  }

  private detectWebRTCSupport(): boolean {
    return !!(
      window.RTCPeerConnection ||
      window.mozRTCPeerConnection ||
      window.webkitRTCPeerConnection
    );
  }

  private detectServiceWorkerSupport(): boolean {
    return "serviceWorker" in navigator;
  }

  private detectIndexedDBSupport(): boolean {
    return "indexedDB" in window;
  }

  private detectLocalStorageSupport(): boolean {
    try {
      return "localStorage" in window && window.localStorage !== null;
    } catch {
      return false;
    }
  }

  private detectBrowserInfo(): {
    name: string;
    version: string;
    engine: string;
  } {
    const ua = navigator.userAgent;
    let name = "Unknown";
    let version = "Unknown";
    let engine = "Unknown";

    if (ua.includes("Firefox")) {
      name = "Firefox";
      engine = "Gecko";
      const match = ua.match(/Firefox\/(\d+)/);
      version = match ? match[1] : "Unknown";
    } else if (ua.includes("Chrome")) {
      name = "Chrome";
      engine = "Blink";
      const match = ua.match(/Chrome\/(\d+)/);
      version = match ? match[1] : "Unknown";
    } else if (ua.includes("Safari")) {
      name = "Safari";
      engine = "WebKit";
      const match = ua.match(/Safari\/(\d+)/);
      version = match ? match[1] : "Unknown";
    } else if (ua.includes("Edge")) {
      name = "Edge";
      engine = "EdgeHTML";
      const match = ua.match(/Edge\/(\d+)/);
      version = match ? match[1] : "Unknown";
    }

    return { name, version, engine };
  }

  private detectOSInfo(): { name: string; version: string } {
    const ua = navigator.userAgent;
    let name = "Unknown";
    let version = "Unknown";

    if (ua.includes("Windows NT")) {
      name = "Windows";
      const match = ua.match(/Windows NT ([\d.]+)/);
      version = match ? match[1] : "Unknown";
    } else if (ua.includes("Mac OS X")) {
      name = "macOS";
      const match = ua.match(/Mac OS X ([\d_]+)/);
      version = match ? match[1].replace(/_/g, ".") : "Unknown";
    } else if (ua.includes("Linux")) {
      name = "Linux";
    } else if (ua.includes("Android")) {
      name = "Android";
      const match = ua.match(/Android ([\d.]+)/);
      version = match ? match[1] : "Unknown";
    } else if (ua.includes("iOS")) {
      name = "iOS";
      const match = ua.match(/OS ([\d_]+)/);
      version = match ? match[1].replace(/_/g, ".") : "Unknown";
    }

    return { name, version };
  }

  private getOptimalTransports(): string[] {
    const isSlowNetwork = this.mobileState.networkState.effectiveType === "2g";

    if (isSlowNetwork || this.mobileState.isLowPowerMode) {
      return ["polling", "websocket"];
    }

    return ["websocket", "polling"];
  }

  private getMobileOptimizedConfig(config: SocketClientConfig): {
    timeout: number;
    reconnectionAttempts: number;
    reconnectionDelay: number;
    reconnectionDelayMax: number;
  } {
    const baseTimeout = config.timeout || 20000;
    const isSlowNetwork =
      this.mobileState.networkState.effectiveType === "2g" ||
      this.mobileState.networkState.effectiveType === "3g";

    return {
      timeout: isSlowNetwork ? baseTimeout * 2 : baseTimeout,
      reconnectionAttempts: this.mobileConfig.adaptiveReconnection
        ? this.mobileState.isLowPowerMode
          ? 3
          : 5
        : config.reconnectionAttempts || this.maxReconnectAttempts,
      reconnectionDelay: config.reconnectionDelay || this.reconnectDelay,
      reconnectionDelayMax: isSlowNetwork ? 10000 : 5000,
    };
  }

  private initializeNetworkMonitoring(): void {
    window.addEventListener("online", () => {
      this.mobileState.networkState.isOnline = true;
      if (this.mobileConfig.enableOfflineMode) {
        this.processOfflineQueue();
      }
    });

    window.addEventListener("offline", () => {
      this.mobileState.networkState.isOnline = false;
    });
  }

  private initializeBackgroundModeDetection(): void {
    document.addEventListener("visibilitychange", () => {
      this.mobileState.isBackgroundMode = document.hidden;

      if (!document.hidden && this.mobileConfig.backgroundSyncEnabled) {
        this.processOfflineQueue();
      }
    });
  }

  private initializeCompressionWorker(): void {
    // Placeholder for compression worker initialization
  }

  private setupAdaptiveSync(): void {
    // Placeholder for adaptive sync setup
  }

  private startMobileOptimizations(): void {
    this.startOperationBuffering();

    if (this.socket?.connected) {
      this.sendClientCapabilities();
    }
  }

  private cleanupMobileMonitors(): void {
    // Cleanup mobile monitors
  }

  // Public API for mobile optimization
  public updateMobileConfig(config: Partial<MobileOptimizationConfig>): void {
    this.mobileConfig = { ...this.mobileConfig, ...config };
  }

  public getMobileState(): MobileState {
    return { ...this.mobileState };
  }

  public forceSyncOfflineQueue(): void {
    if (this.mobileConfig.enableOfflineMode) {
      this.processOfflineQueue();
    }
  }

  // Session recovery methods

  /**
   * Store client state for potential session recovery
   */
  private storeClientStateForRecovery(): void {
    // Get current canvas state from collaboration store if available
    const collaborationStore = window.__collaborationStore;

    this.clientState = {
      canvasState: collaborationStore?.getCanvasState?.(),
      selectionState: collaborationStore?.getSelectionState?.(),
      cursorPosition: collaborationStore?.getCursorPosition?.(),
    };

    logger.debug("Client state stored for recovery", {
      sessionId: this.sessionId,
      hasCanvasState: !!this.clientState?.canvasState,
      hasSelectionState: !!this.clientState?.selectionState,
      hasCursorPosition: !!this.clientState?.cursorPosition,
    });
  }

  /**
   * Check if session recovery should be attempted
   */
  private shouldAttemptSessionRecovery(): boolean {
    // Don't attempt recovery if:
    // - No session ID exists
    // - No disconnection timestamp
    // - Already recovering
    // - Max attempts reached
    // - Disconnection was too long ago (> 5 minutes)

    if (!this.sessionId || !this.disconnectedAt || this.isRecoveringSession) {
      return false;
    }

    if (this.sessionRecoveryAttempts >= this.maxSessionRecoveryAttempts) {
      logger.warn("Max session recovery attempts reached");
      return false;
    }

    const disconnectionDuration = Date.now() - this.disconnectedAt;
    const maxRecoveryWindow = 5 * 60 * 1000; // 5 minutes

    if (disconnectionDuration > maxRecoveryWindow) {
      logger.info("Session recovery window expired", {
        duration: disconnectionDuration,
        maxWindow: maxRecoveryWindow,
      });
      return false;
    }

    return true;
  }

  /**
   * Perform session recovery after reconnection
   */
  private async performSessionRecovery(): Promise<void> {
    if (!this.socket?.connected || !this.sessionId || !this.disconnectedAt) {
      logger.error("Cannot perform session recovery: missing requirements");
      this.emit("reconnected");
      return;
    }

    this.isRecoveringSession = true;
    this.sessionRecoveryAttempts++;

    logger.info("Starting session recovery", {
      sessionId: this.sessionId,
      attempt: this.sessionRecoveryAttempts,
      disconnectedAt: new Date(this.disconnectedAt),
      duration: Date.now() - this.disconnectedAt,
    });

    // Emit recovery started event
    this.emit("sessionRecoveryStarted");

    try {
      // Prepare recovery data
      const recoveryData: SessionRecoveryData = {
        sessionId: this.sessionId,
        lastKnownVectorClock: this.lastKnownVectorClock || {},
        lastOperationId: this.lastOperationId || undefined,
        disconnectedAt: this.disconnectedAt,
        clientState: this.clientState || undefined,
      };

      // Emit session recovery request
      this.socket.emit("session-recovery", recoveryData);

      // Set timeout for recovery response
      const recoveryTimeout = setTimeout(() => {
        this.handleSessionRecoveryTimeout();
      }, 10000); // 10 second timeout

      // Store timeout to clear it later
      this.recoveryTimeout = recoveryTimeout;
    } catch (error) {
      logger.error("Session recovery failed", error);
      this.isRecoveringSession = false;
      this.emit(
        "sessionRecoveryFailed",
        normalizeError(error, "Session recovery failed"),
      );
      this.emit("reconnected");
    }
  }

  /**
   * Handle session recovery response from server
   */
  private handleSessionRecoveryResponse(
    response: SessionRecoveryResponse,
  ): void {
    // Clear recovery timeout if exists
    const timeout = this.recoveryTimeout;
    if (timeout) {
      clearTimeout(timeout);
      this.recoveryTimeout = null;
    }

    this.isRecoveringSession = false;

    if (!response.success) {
      logger.error("Session recovery failed", {
        error: response.error,
        message: response.message,
      });

      this.emit(
        "sessionRecoveryFailed",
        new Error(response.error || response.message || "Unknown error"),
      );

      // Reset session state on failure
      this.sessionId = null;
      this.lastKnownVectorClock = {};
      this.lastOperationId = null;
      this.sessionRecoveryAttempts = 0;

      // Regular reconnection
      this.emit("reconnected");
      return;
    }

    logger.info("Session recovery successful", {
      sessionId: response.sessionId,
      missedOperations: response.missedOperations.length,
      activeUsers: response.activeUsers.length,
      conflicts: response.conflictsDetected.length,
      duration: response.recoveryDuration,
    });

    // Update vector clock
    if (response.currentVectorClock) {
      this.lastKnownVectorClock = response.currentVectorClock;
    }

    // Process missed operations
    let processedCount = 0;
    const totalOperations = response.missedOperations.length;
    const sessionId = this.sessionId;
    const projectId = this.currentProjectId;

    if (!sessionId || !projectId) {
      this.emit(
        "sessionRecoveryFailed",
        new Error(
          "Session recovery completed without an active project session",
        ),
      );
      this.emit("reconnected");
      return;
    }

    for (const operation of response.missedOperations) {
      // Emit operation as if it was received normally
      const message: CollaborationMessage = {
        type: "collaboration",
        payload: {
          operation,
          sessionId,
        },
        userId: operation.userId,
        projectId,
        timestamp: Date.now(),
      };
      this.emit("operationReceived", message);

      processedCount++;

      // Emit progress updates
      const progress = (processedCount / totalOperations) * 100;
      this.emit(
        "sessionRecoveryProgress",
        progress,
        `Processing operation ${processedCount}/${totalOperations}`,
      );
    }

    // Restore user presence and cursors
    for (const user of response.activeUsers) {
      if (user.cursor) {
        const cursorMessage: CursorMessage = {
          type: "cursor",
          payload: {
            position: user.cursor,
            sessionId,
          },
          userId: user.userId,
          projectId,
          timestamp: Date.now(),
        };
        this.emit("cursorUpdate", cursorMessage);
      }

      if (user.selection) {
        const selectionMessage: SelectionMessage = {
          type: "selection",
          payload: {
            selection: user.selection,
            sessionId,
          },
          userId: user.userId,
          projectId,
          timestamp: Date.now(),
        };
        this.emit("selectionUpdate", selectionMessage);
      }
    }

    // Handle detected conflicts when present
    if (response.conflictsDetected.length > 0) {
      logger.warn("Conflicts detected during recovery", {
        count: response.conflictsDetected.length,
        conflicts: response.conflictsDetected,
      });

      // Conflicts will be handled by the collaboration store
      // through the normal conflict resolution mechanisms
    }

    // Reset recovery attempts on success
    this.sessionRecoveryAttempts = 0;
    this.disconnectedAt = null;
    this.clientState = null;

    // Emit completion events
    this.emit("sessionRecoveryCompleted", response.missedOperations.length);
    this.emit("reconnected");

    // Trigger haptic feedback on mobile
    if (this.isMobileDevice()) {
      hapticFeedbackService.trigger("sync-complete");
    }
  }

  /**
   * Handle session recovery timeout
   */
  private handleSessionRecoveryTimeout(): void {
    logger.error("Session recovery timeout");

    this.isRecoveringSession = false;

    this.emit("sessionRecoveryFailed", new Error("Session recovery timeout"));

    // Try regular reconnection
    this.emit("reconnected");
  }

  /**
   * Manually trigger session recovery (for testing or forced recovery)
   */
  public forceSessionRecovery(): void {
    if (!this.socket?.connected) {
      logger.error("Cannot force session recovery: not connected");
      return;
    }

    if (this.isRecoveringSession) {
      logger.warn("Session recovery already in progress");
      return;
    }

    // Set artificial disconnection timestamp if not set
    if (!this.disconnectedAt) {
      this.disconnectedAt = Date.now() - 5000; // 5 seconds ago
    }

    this.performSessionRecovery();
  }

  /**
   * Get session recovery state
   */
  public getSessionRecoveryState(): {
    isRecovering: boolean;
    attempts: number;
    maxAttempts: number;
    sessionId: string | null;
    lastOperationId: string | null;
    disconnectedAt: number | null;
  } {
    return {
      isRecovering: this.isRecoveringSession,
      attempts: this.sessionRecoveryAttempts,
      maxAttempts: this.maxSessionRecoveryAttempts,
      sessionId: this.sessionId,
      lastOperationId: this.lastOperationId,
      disconnectedAt: this.disconnectedAt,
    };
  }

  /**
   * Request conflict auto-resolution from server
   */
  public requestConflictAutoResolve(
    conflictId: string,
    conflict: VersionConflict,
    options?: {
      preferredStrategy?: "USE_SOURCE" | "USE_TARGET" | "MERGE" | "ESCALATE";
      userPreferences?: {
        preferNewest?: boolean;
        preferOwn?: boolean;
        preserveIntention?: boolean;
      };
      confidenceThreshold?: number;
    },
  ): void {
    if (!this.socket?.connected) {
      logger.error("Cannot request conflict auto-resolve: not connected");
      this.emit(
        "conflictAutoResolveFailed",
        conflictId,
        new Error("Not connected"),
      );
      return;
    }

    if (!this.currentProjectId) {
      logger.error("Cannot request conflict auto-resolve: not in a project");
      this.emit(
        "conflictAutoResolveFailed",
        conflictId,
        new Error("Not in a project"),
      );
      return;
    }

    logger.info("Requesting conflict auto-resolution", {
      conflictId,
      preferredStrategy: options?.preferredStrategy,
      confidenceThreshold: options?.confidenceThreshold,
    });

    // Emit auto-resolve started event
    this.emit("conflictAutoResolveStarted", conflictId);

    try {
      const resolveData: ConflictAutoResolveData = {
        conflictId,
        conflict,
        preferredStrategy: options?.preferredStrategy,
        userPreferences: options?.userPreferences,
        confidenceThreshold: options?.confidenceThreshold || 0.7,
      };

      this.socket.emit("conflict-auto-resolve", resolveData);
    } catch (error) {
      logger.error("Failed to emit conflict auto-resolve request", error);
      this.emit(
        "conflictAutoResolveFailed",
        conflictId,
        normalizeError(error, "Failed to request conflict auto-resolution"),
      );
    }
  }

  /**
   * Handle conflict auto-resolve response from server
   */
  private handleConflictAutoResolveResponse(
    result: ConflictAutoResolveResult,
  ): void {
    logger.info("Conflict auto-resolve response received", {
      conflictId: result.conflictId,
      resolved: result.resolved,
      strategy: result.strategy,
      confidence: result.confidence,
      requiresManualReview: result.requiresManualReview,
    });

    if (result.resolved && !result.requiresManualReview) {
      // Successful auto-resolution
      this.emit("conflictAutoResolveCompleted", result);
    } else if (result.requiresManualReview) {
      // Auto-resolution suggests manual review
      logger.info("Conflict requires manual review", {
        conflictId: result.conflictId,
        reasoning: result.reasoning,
      });
      this.emit("conflictAutoResolveCompleted", result);
    } else {
      // Auto-resolution failed
      const error = new Error(result.reasoning || "Auto-resolution failed");
      logger.warn("Conflict auto-resolution failed", {
        conflictId: result.conflictId,
        reasoning: result.reasoning,
      });
      this.emit("conflictAutoResolveFailed", result.conflictId, error);
    }
  }

  /**
   * Send client capabilities to server for negotiation
   */
  public sendClientCapabilities(): void {
    if (!this.socket?.connected) {
      logger.error("Cannot send client capabilities: not connected");
      this.emit("clientCapabilitiesError", new Error("Not connected"));
      return;
    }

    try {
      // Detect current capabilities
      this.clientCapabilities = this.detectClientCapabilities();
      const capabilities = this.clientCapabilities;

      logger.info("Sending client capabilities", {
        deviceType: capabilities.deviceType,
        inputMethod: capabilities.inputMethod,
        performance: capabilities.performance,
        features: Object.entries(capabilities.features)
          .filter(([, enabled]) => enabled)
          .map(([feature]) => feature),
      });

      this.socket.emit("client-capabilities", capabilities);
    } catch (error) {
      logger.error("Failed to send client capabilities", error);
      this.emit(
        "clientCapabilitiesError",
        normalizeError(error, "Failed to send client capabilities"),
      );
    }
  }

  /**
   * Handle client capabilities response from server
   */
  private handleClientCapabilitiesResponse(
    optimizations: CapabilityOptimizations,
  ): void {
    logger.info("Client capabilities negotiated", {
      maxOperationsPerBatch: optimizations.maxOperationsPerBatch,
      operationThrottleMs: optimizations.operationThrottleMs,
      renderingQuality: optimizations.renderingQuality,
      enableOfflineMode: optimizations.enableOfflineMode,
      enableHardwareAcceleration: optimizations.enableHardwareAcceleration,
    });

    // Store negotiated optimizations
    this.negotiatedOptimizations = optimizations;
    this.capabilitiesNegotiated = true;

    // Apply optimizations to current configuration
    this.applyNegotiatedOptimizations(optimizations);

    // Emit success event
    this.emit("clientCapabilitiesNegotiated", optimizations);
  }

  /**
   * Apply negotiated optimizations to client configuration
   */
  private applyNegotiatedOptimizations(
    optimizations: CapabilityOptimizations,
  ): void {
    // Update mobile configuration based on negotiated optimizations
    this.mobileConfig.enableOfflineMode = optimizations.enableOfflineMode;
    this.mobileConfig.compressionEnabled = optimizations.compressionLevel > 0;

    // Update buffer size based on performance recommendations
    if (optimizations.maxOperationsPerBatch) {
      // Adjust operation buffer size
      this.operationBuffer = [];
    }

    logger.debug("Applied negotiated optimizations", optimizations);
  }

  /**
   * Get current client capabilities
   */
  public getClientCapabilities(): ClientCapabilities | null {
    return this.clientCapabilities;
  }

  /**
   * Get negotiated optimizations
   */
  public getNegotiatedOptimizations(): CapabilityOptimizations | null {
    return this.negotiatedOptimizations;
  }

  /**
   * Check if capabilities have been negotiated
   */
  public areCapabilitiesNegotiated(): boolean {
    return this.capabilitiesNegotiated;
  }

  /**
   * Handle batch operation result from server
   */
  private handleBatchOperationResult(result: BatchOperationResult): void {
    logger.info("Batch operation result received", {
      batchId: result.batchId,
      success: result.success,
      processedOperations: result.processedOperations,
      failedOperations: result.failedOperations,
      processingTime: result.totalProcessingTime,
      conflictsDetected: result.conflictsDetected.length,
    });

    if (result.success) {
      // Process successful results
      const transformedOperations = result.results.flatMap((operationResult) =>
        operationResult.success && operationResult.transformedOperation
          ? [operationResult.transformedOperation]
          : [],
      );

      if (transformedOperations.length > 0) {
        logger.debug("Applying transformed operations from batch", {
          batchId: result.batchId,
          transformedCount: transformedOperations.length,
        });

        // Update vector clock if operations were transformed
        transformedOperations.forEach((op) => {
          if (op.vector) {
            this.updateVectorClock(op.vector);
          }
          if (op.id) {
            this.lastOperationId = op.id;
          }
        });
      }

      // Handle conflicts if detected
      if (result.conflictsDetected.length > 0) {
        logger.warn("Conflicts detected in batch operations", {
          batchId: result.batchId,
          conflictCount: result.conflictsDetected.length,
        });
      }

      this.emit("batchOperationCompleted", result);
    } else {
      // Handle batch failure
      const errorMessage = `Batch operation failed: ${result.failedOperations}/${result.processedOperations + result.failedOperations} operations failed`;
      logger.error("Batch operation failed", {
        batchId: result.batchId,
        errorMessage,
        failedResults: result.results.filter((r) => !r.success),
      });

      this.emit(
        "batchOperationFailed",
        result.batchId,
        new Error(errorMessage),
      );
    }
  }

  /**
   * Update session ID (used when joining a project)
   */
  public updateSessionId(sessionId: string): void {
    this.sessionId = sessionId;
    logger.debug("Session ID updated", { sessionId });
  }

  /**
   * Update vector clock (used by collaboration store)
   */
  public updateVectorClock(vectorClock: VectorClock): void {
    this.lastKnownVectorClock = { ...vectorClock };
  }

  /**
   * Get current vector clock
   */
  public getVectorClock(): VectorClock {
    return { ...this.lastKnownVectorClock };
  }
}

// Export secure singleton instance with WSS enforcement
export const socketClient = SocketClient.getInstance();

// Types are already exported as interfaces above
